/**
 * Replacement plan line import: upload → editable preview → commit.
 *
 * Functional spec: `docs/features/REPLACEMENT-PLANNING.md` (CSV format) and
 * `utils/replacement_import.py`. Mirrors `CatalogImport.tsx`'s upload/preview
 * /commit shape, scaled down to this format's much smaller column set.
 *
 * One rule this modal exists to enforce that `CatalogImport.tsx` does not:
 * `POST /{plan_id}/import/commit` answers **422 for the whole request** if
 * any *selected* row still has no resolved item, rather than silently
 * skipping it the way the catalog importer skips an unresolved manufacturer.
 * So unlike the catalog modal, "commit" has to be disabled outright while
 * any selected row is unresolved — there is no partial save to fall back on.
 */
import { useMemo, useState } from 'react'
import {
  Alert, Badge, Button, Checkbox, FileInput, Group, Paper, Stack, Text,
} from '@mantine/core'
import { useMutation } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import type { ColumnDef } from '@tanstack/react-table'

import { ApiError, api } from '../api/client'
import type { PlanComputed, ReplacementImportRow, ReplacementImportUpload } from '../api/types'
import { Modal } from '../components/AppModal'
import { DataGrid } from '../components/DataGrid'
import { EM_DASH, fmtQty } from '../format'
import { CODE_STYLE } from '../theme'
import { ItemPicker } from './ItemPicker'

interface Props {
  opened: boolean
  planId: number
  onClose: () => void
  /** The commit response IS the recomputed plan (same "write response equals
   * the next GET" contract every other write on this screen relies on) — the
   * caller writes it straight into its query cache. */
  onCommitted: (computed: PlanComputed) => void
}

export function ReplacementImportModal({ opened, planId, onClose, onCommitted }: Props) {
  const [file, setFile] = useState<File | null>(null)
  const [result, setResult] = useState<ReplacementImportUpload | null>(null)
  const [rows, setRows] = useState<ReplacementImportRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [resolvingIndex, setResolvingIndex] = useState<number | null>(null)

  const upload = useMutation({
    mutationFn: (f: File) =>
      api.upload<ReplacementImportUpload>('/replacement-plans/import/upload', f),
    onSuccess: (data) => {
      setResult(data)
      // Flagged rows first, same reasoning CatalogImportModal uses: a preview
      // with the problems scattered through it is a preview nobody reads.
      setRows([...data.rows].sort((a, b) => Number(b.needs_attention) - Number(a.needs_attention)))
      setError(null)
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'קריאת הקובץ נכשלה.'),
  })

  const commit = useMutation({
    mutationFn: () =>
      api.post<PlanComputed>(`/replacement-plans/${planId}/import/commit`, {
        upload_id: result?.upload_id ?? '', rows,
      }),
    onSuccess: (computed) => {
      notifications.show({
        color: 'green',
        message: `${rows.filter((r) => r.selected).length} שורות נוספו לתוכנית`,
      })
      onCommitted(computed)
      close()
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'שמירת הייבוא נכשלה.'),
  })

  function close() {
    // No discard-on-abandon route exists for this importer (unlike catalog
    // import's `/catalog-import/{upload_id}`) — by design, per
    // webapi/routers/replacement.py: the temp file persists until a
    // successful commit discards it, or the daily sweep does. Nothing to
    // call here.
    setFile(null); setResult(null); setRows([]); setError(null)
    setResolvingIndex(null)
    onClose()
  }

  function patch(index: number, change: Partial<ReplacementImportRow>) {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...change } : r)))
  }

  const selectedCount = rows.filter((r) => r.selected).length
  const unresolved = rows.filter((r) => r.selected && r.new_item_id == null)

  const columns = useMemo<ColumnDef<ReplacementImportRow, any>[]>(
    () => [
      {
        id: 'sel',
        header: '',
        cell: (c) => (
          <Checkbox
            checked={c.row.original.selected}
            onChange={(e) =>
              patch(rows.indexOf(c.row.original), { selected: e.currentTarget.checked })
            }
            aria-label="כלול שורה"
            size="md"
          />
        ),
      },
      {
        id: 'flag',
        header: '',
        cell: (c) =>
          c.row.original.needs_attention ? (
            <Badge size="xs" color="red" variant="light" title={c.row.original.reason}>
              ⚠
            </Badge>
          ) : null,
      },
      { accessorKey: 'old_label', header: 'ציוד ישן', cell: (c) => c.getValue<string>() || EM_DASH },
      {
        accessorKey: 'old_qty', header: 'כמות', meta: { numeric: true },
        cell: (c) => fmtQty(c.getValue<number>()),
      },
      { accessorKey: 'new_sku', header: 'מק"ט חדש', meta: { code: true } },
      {
        id: 'item',
        header: 'פריט מזוהה',
        cell: (c) => {
          const row = c.row.original
          return row.new_item_id != null ? (
            <Text size="sm">{row.new_item_name}</Text>
          ) : (
            <Button size="xs" variant="light" color="orange"
                    onClick={() => setResolvingIndex(rows.indexOf(row))}>
              בחר פריט…
            </Button>
          )
        },
      },
      {
        accessorKey: 'ratio', header: 'יחס', meta: { numeric: true },
        cell: (c) => fmtQty(c.getValue<number>()),
      },
      {
        accessorKey: 'install_cost_per_unit', header: 'עלות התקנה ליח׳', meta: { numeric: true },
        cell: (c) => fmtQty(c.getValue<number>()),
      },
      { accessorKey: 'notes', header: 'הערות', cell: (c) => c.getValue<string>() || EM_DASH },
    ],
    [rows],
  )

  return (
    <>
      <Modal opened={opened} onClose={close} title="ייבוא CSV — ציוד להחלפה" size="90%">
        <Stack>
          {error && <Alert color="red" variant="light">{error}</Alert>}

          {!result && (
            <>
              <Alert color="blue" variant="light" title="פורמט הקובץ">
                <Text size="sm">
                  שורת כותרת, UTF-8. עמודות חובה: <b style={CODE_STYLE}>old_label, old_qty,
                  new_sku</b>. עמודות אופציונליות: <b style={CODE_STYLE}>old_sku, ratio,
                  install_cost_per_unit, notes</b>.
                </Text>
              </Alert>
              <FileInput
                label="קובץ CSV"
                placeholder="בחר קובץ…"
                accept=".csv"
                value={file}
                onChange={(f) => { setFile(f); setError(null) }}
              />
              {file && (
                <Button onClick={() => upload.mutate(file)} loading={upload.isPending}>
                  קרא קובץ
                </Button>
              )}
            </>
          )}

          {result && (
            <>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  {rows.length.toLocaleString('he-IL')} שורות · {selectedCount.toLocaleString('he-IL')} נבחרו
                </Text>
                <Group gap="xs">
                  <Button size="xs" variant="subtle" onClick={() => setRows((r) => r.map((x) => ({ ...x, selected: true })))}>
                    בחר הכל
                  </Button>
                  <Button size="xs" variant="subtle" onClick={() => setRows((r) => r.map((x) => ({ ...x, selected: false })))}>
                    נקה בחירה
                  </Button>
                </Group>
              </Group>

              {result.needs_attention > 0 && (
                <Alert color="red" variant="light">
                  {result.needs_attention} שורות דורשות תשומת לב — לרוב מק"ט שלא זוהה בקטלוג.
                  בחר פריט לכל שורה כזו, או בטל את הסימון שלה.
                </Alert>
              )}

              <Paper withBorder>
                <DataGrid data={rows} columns={columns} maxHeight="45vh" stickyFirstColumn={false} />
              </Paper>

              <Group justify="space-between" align="center">
                {unresolved.length > 0 && (
                  <Text size="sm" c="red">
                    {unresolved.length} שורות נבחרות עדיין ללא פריט מזוהה — פתור אותן או בטל
                    את הסימון שלהן לפני השמירה.
                  </Text>
                )}
                <Group ml="auto">
                  <Button variant="default" onClick={close}>ביטול</Button>
                  <Button
                    onClick={() => commit.mutate()}
                    loading={commit.isPending}
                    disabled={!selectedCount || unresolved.length > 0}
                  >
                    הוסף {selectedCount.toLocaleString('he-IL')} שורות לתוכנית
                  </Button>
                </Group>
              </Group>
            </>
          )}
        </Stack>
      </Modal>

      <ItemPicker
        opened={resolvingIndex != null}
        onClose={() => setResolvingIndex(null)}
        onPick={(items) => {
          const item = items[0]
          if (item && resolvingIndex != null) {
            patch(resolvingIndex, { new_item_id: item.id!, new_item_name: item.name, selected: true })
          }
          setResolvingIndex(null)
        }}
        title="בחירת פריט"
        confirmLabel="בחר"
      />
    </>
  )
}
